import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight, ClockCounterClockwise } from "@phosphor-icons/react";

function renderBody(body) {
  // Minimal Markdown-ish: paragraphs, headings, bullets
  if (!body) return null;
  const lines = body.split("\n");
  const blocks = [];
  let para = [];
  let bullets = [];
  const flushPara = () => { if (para.length) { blocks.push({ type: "p", text: para.join(" ") }); para = []; } };
  const flushBullets = () => { if (bullets.length) { blocks.push({ type: "ul", items: bullets }); bullets = []; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushBullets(); continue; }
    if (line.startsWith("# ")) { flushPara(); flushBullets(); blocks.push({ type: "h1", text: line.slice(2) }); }
    else if (line.startsWith("## ")) { flushPara(); flushBullets(); blocks.push({ type: "h2", text: line.slice(3) }); }
    else if (line.startsWith("- ") || line.startsWith("* ")) { flushPara(); bullets.push(line.slice(2)); }
    else { flushBullets(); para.push(line); }
  }
  flushPara(); flushBullets();
  return blocks.map((b, i) => {
    if (b.type === "h1") return <h2 key={i} className="font-heading text-3xl font-bold text-stone-900 mt-8 mb-3">{b.text}</h2>;
    if (b.type === "h2") return <h3 key={i} className="font-heading text-2xl font-bold text-stone-900 mt-6 mb-2">{b.text}</h3>;
    if (b.type === "ul") return <ul key={i} className="list-disc pl-6 space-y-1.5 text-stone-700 my-4">{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>;
    return <p key={i} className="text-stone-700 leading-relaxed mb-4">{b.text}</p>;
  });
}

export default function TipDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null));
  }, [slug]);

  if (post === undefined) return <div className="min-h-screen p-12 text-stone-500">Loading…</div>;
  if (post === null) return <Navigate to="/tips" replace />;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo title={`${post.title} | Puppl Training Tips`} description={post.excerpt || post.title} canonical={typeof window !== "undefined" ? window.location.origin + `/tips/${post.slug}` : ""} />
      <PublicHeader />

      <article className="max-w-3xl mx-auto px-6 lg:px-12 py-12">
        <Link to="/tips" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#f38838] mb-6"><ArrowLeft size={14} /> All tips</Link>
        <div className="flex items-center gap-3 mb-3 text-xs">
          <span className="bg-[#f38838]/15 text-[#f38838] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider">{post.category}</span>
          <span className="text-stone-500 flex items-center gap-1"><ClockCounterClockwise size={12} /> {post.read_minutes} min read</span>
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-bold text-stone-900 leading-tight mb-3" data-testid="tip-title">{post.title}</h1>
        {post.excerpt && <p className="text-lg text-stone-600 mb-8">{post.excerpt}</p>}
        {post.cover_image && (
          <img loading="lazy" src={post.cover_image} alt={post.title} className="rounded-2xl w-full aspect-[16/9] object-cover mb-10" />
        )}
        <div className="prose prose-stone max-w-none">{renderBody(post.body)}</div>

        <div className="mt-12 card-pp p-8 bg-stone-100 text-center">
          <h2 className="font-heading text-xl font-bold text-stone-900 mb-2">Want hands-on help with your puppy?</h2>
          <p className="text-stone-600 mb-5">Book a session with the team — Sunshine Coast puppy specialist.</p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2">Book a Free Consult <ArrowRight size={16} weight="bold" /></Link>
        </div>
      </article>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
